<template>
    <div class="container admin-table-page">
        <!-- ===== HEADER ===== -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- ===== FILTROS ===== -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">

                    <b-form-select
                        id="validity-select"
                        v-model="selVigencia"
                        @change="onVigenciaChange"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option
                            v-for="vigencia in listVigencia"
                            :key="vigencia"
                            :value="vigencia"
                        >
                            {{ vigencia }}
                        </option>
                    </b-form-select>


                    <b-form-select
                        id="report-select"
                        v-model="selReporte"
                        @change="onReportChange"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option
                            v-for="reporte in listReporte"
                            :key="reporte.reporte"
                            :value="reporte.reporte"
                        >
                            {{ reporte.descripcion }}
                        </option>
                    </b-form-select>

                    <b-button
                        class="consult-btn ml-2"
                        @click="consultar()"
                        :disabled="!selReporte || !selVigencia || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- ===== TARJETA ÚNICA ===== -->
        <div
            class="admin-table-block mt-4"
            :class="{ 'resumen-card': !isLoading && fgl_consulta && bloquesData.length }"
        >
            <!-- ===== ESTADO INICIAL ===== -->
            <div v-if="!fgl_consulta && !isLoading">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">{{ labels.selectFilters }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- ===== LOADING ===== -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- ===== RESULTADOS ===== -->
            <div v-if="!isLoading && fgl_consulta">
                <!-- Sin datos -->
                <div v-if="!bloquesData.length" class="text-center py-5">
                    <font-awesome-icon
                        :icon="['fas', 'table']"
                        size="3x"
                        class="text-muted mb-3"
                        style="opacity: 0.3;"
                    />
                    <p class="h4 text-muted">{{ labels.noData }}</p>
                    <p class="text-muted">{{ labels.noResults }}</p>
                </div>

                <!-- Barra de acciones + bloques -->
                <div v-else>
                    <!-- Barra con botón de exportar -->
                    <div class="resumen-toolbar">
                        <div class="resumen-toolbar-title"></div>
                        <div class="resumen-toolbar-actions">
                            <b-button
                                variant="success"
                                size="sm"
                                class="table-toolbar-export-btn"
                                @click="exportarExcel"
                                :disabled="!bloquesData.length"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                                {{ labels.export }}
                            </b-button>
                        </div>
                    </div>

                    <!-- Bloques con scroll interno -->
                    <div class="bloques-scroll">
                        <div
                            v-for="bloque in bloquesData"
                            :key="bloque.codigo"
                            class="resumen-bloque"
                        >
                            <h5 class="bloque-titulo">
                                {{ bloque.codigoLabel }} (codigo {{ bloque.codigoLabel }}-{{ bloque.descripcion }})
                            </h5>

                            <CorporateDataTable
                                :items="bloque.items"
                                :fields="bloque.fields"
                                :labels="tableLabels"
                                :narrow="false"
                                :table-min-height="0"
                                :table-max-height="'none'"
                                :show-search="false"
                                :allow-column-toggle="false"
                                :enable-sorting="false"
                                :show-pagination="false"
                                :show-rows-per-page="false"
                            >
                                <template #cell(etiqueta)="data">
                                    <div
                                        class="text-left font-weight-bold"
                                        :class="{ 'fila-total': data.item?._isTotal }"
                                    >
                                        {{ data.item?.etiqueta }}
                                    </div>
                                </template>

                                <template
                                    v-for="m in municipios"
                                    #[`cell(mun_${m.municipio})`]="data"
                                >
                                    <div
                                        :key="m.municipio"
                                        class="text-right"
                                        :class="{ 'fila-total': data.item?._isTotal }"
                                    >
                                        {{ formatMoney(data.item?.valores?.[m.municipio]) }}
                                    </div>
                                </template>

                                <template #cell(total)="data">
                                    <div
                                        class="text-right font-weight-bold"
                                        :class="{ 'fila-total': data.item?._isTotal }"
                                    >
                                        {{ formatMoney(data.item?.total) }}
                                    </div>
                                </template>
                            </CorporateDataTable>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as XLSX from 'exceljs'
import ResumenInfoService from '@/services/ResumenInfo'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: 'ResumenInfo',
    components: {
        CorporateDataTable
    },
    data: () => ({
        fgl_consulta: false,
        isLoading: false,
        listVigencia: [],
        listReporte: [],
        selVigencia: '',
        selReporte: '',
        municipios: [],
        bloquesRaw: [],
        bloquesData: [],
        nit: ''
    }),
    computed: {
        labels() {
            if (this.$store.state._language == 'es-ES') {
                return {
                    title: 'Resumen de Ingresos',
                    selectReport: 'Seleccione Reporte',
                    selectValidity: 'Seleccione Vigencia',
                    consultar: 'Consultar',
                    loading: 'Cargando...',
                    noData: 'No hay datos disponibles',
                    noResults: 'No se encontraron resultados',
                    selectFilters: 'Seleccione los filtros y consulte',
                    noMatches: 'No se encontraron resultados',
                    searchPlaceholder: 'Buscar en la tabla...',
                    rowsPerPage: 'Filas por página:',
                    noSort: 'Sin orden',
                    sortAsc: 'A-Z / Menor a mayor',
                    sortDesc: 'Z-A / Mayor a menor',
                    columns: 'Columnas',
                    showAllColumns: 'Mostrar todas',
                    filters: 'Filtros',
                    clearFilters: 'Limpiar filtros',
                    export: 'Exportar Excel',
                    etiqueta: 'CIIU',
                    totalGeneral: 'Total general'
                }
            }
            return {
                title: 'Income Summary',
                selectReport: 'Select Report',
                selectValidity: 'Select Validity',
                consultar: 'Consult',
                loading: 'Loading...',
                noData: 'No data available',
                noResults: 'No results found',
                selectFilters: 'Select filters and consult',
                noMatches: 'No results found',
                searchPlaceholder: 'Search in table...',
                rowsPerPage: 'Rows per page:',
                noSort: 'No sorting',
                sortAsc: 'A-Z / Low to high',
                sortDesc: 'Z-A / High to low',
                columns: 'Columns',
                showAllColumns: 'Show all',
                filters: 'Filters',
                clearFilters: 'Clear filters',
                export: 'Exportar Excel',
                etiqueta: 'CIIU',
                totalGeneral: 'Total general'
            }
        },

        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        }
    },
    watch: {
        bloquesRaw: {
            immediate: true,
            handler() { this.recalcularBloques() }
        },
        municipios: {
            deep: true,
            handler() { this.recalcularBloques() }
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        onReportChange() {
            this.bloquesRaw = []
            this.bloquesData = []
            this.fgl_consulta = false
        },

        onVigenciaChange() {
            this.bloquesRaw = []
            this.bloquesData = []
            this.fgl_consulta = false
        },

        formatMoney(value) {
            const num = Number(value) || 0
            return num.toLocaleString('es-CO', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        },

        recalcularBloques() {
            this.bloquesData = (this.bloquesRaw || []).map((bloque) => {
                const fields = [
                    {
                        key: 'etiqueta',
                        label: this.labels.etiqueta,
                        thClass: 'text-left',
                        tdClass: 'text-left align-middle'
                    },
                    ...this.municipios.map((m) => ({
                        key: `mun_${m.municipio}`,
                        label: m.nombre,
                        thClass: 'text-center',
                        tdClass: 'text-right align-middle'
                    })),
                    {
                        key: 'total',
                        label: this.labels.totalGeneral,
                        thClass: 'text-center',
                        tdClass: 'text-right align-middle'
                    }
                ]

                const limpiar = (v) =>
                    v && String(v).toLowerCase() !== 'null' ? v : null

                const filas = (bloque.filas || []).map((f) => ({
                    etiqueta: limpiar(f.alternoLabel) ?? limpiar(f.alterno) ?? '-',
                    valores: f.valores,
                    total: f.total,
                    _isTotal: false
                }))

                const filaTotal = {
                    etiqueta: this.labels.totalGeneral,
                    valores: bloque.totales,
                    total: bloque.totales?.total || 0,
                    _isTotal: true
                }

                return {
                    codigo: bloque.codigo,
                    codigoLabel: bloque.codigoLabel,
                    descripcion: bloque.descripcion,
                    fields,
                    items: [...filas, filaTotal]
                }
            })
        },

        consultar() {
            if (!this.selVigencia || !this.selReporte) {
                this.$toast.error('Seleccione un reporte y una vigencia.')
                return
            }
            if (!this.nit) {
                this.$toast.error('No se identificó la compañía (nit).')
                return
            }

            this.isLoading = true
            ResumenInfoService.getResumen({
                vigencia: this.selVigencia,
                reporte: this.selReporte,
                nit: this.nit
            })
                .then((res) => {
                    const data = res.data.data || {}
                    this.municipios = data.municipios || []
                    this.bloquesRaw = data.bloques || []
                    this.fgl_consulta = true
                })
                .catch((err) => {
                    console.error(err)
                    this.municipios = []
                    this.bloquesRaw = []
                    this.fgl_consulta = true
                    this.$toast.error('Error al consultar el resumen')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        cargarCombos() {
            this.isLoading = true
            Promise.all([
                ResumenInfoService.getVigencias(),
                ResumenInfoService.getReportes()
            ])
                .then(([vigRes, repRes]) => {
                    this.listVigencia = vigRes.data.vigencias || []
                    this.listReporte = repRes.data.reportes || []
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar filtros')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        /* ============================================================
         * EXPORTAR A EXCEL — UNA SOLA HOJA
         * ============================================================ */
        async exportarExcel() {
            // Flag NO reactivo para evitar doble clic sin re-render
            if (this._exportandoFlag) return
            if (!this.bloquesData.length) {
                this.$toast.warning('No hay datos para exportar')
                return
            }

            this._exportandoFlag = true

            try {
                // Copia plana para no tocar la reactividad
                const bloquesCopia = JSON.parse(JSON.stringify(this.bloquesData))
                const municipiosCopia = JSON.parse(JSON.stringify(this.municipios))

                const { Workbook } = XLSX
                const workbook = new Workbook()
                const worksheet = workbook.addWorksheet('Resumen de Ingresos')

                const columnCount = 2 + municipiosCopia.length // CIIU + municipios + Total

                // Estilos
                const styles = {
                    titulo: {
                        font: { name: 'Calibri', size: 16, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'center', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } },
                        border: {
                            top: { style: 'thin' }, left: { style: 'thin' },
                            bottom: { style: 'thin' }, right: { style: 'thin' }
                        }
                    },
                    subtitulo: {
                        font: { name: 'Calibri', size: 11, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'center', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } },
                        border: {
                            top: { style: 'thin' }, left: { style: 'thin' },
                            bottom: { style: 'thin' }, right: { style: 'thin' }
                        }
                    },
                    bloqueTitulo: {
                        font: { name: 'Calibri', size: 12, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'left', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9EAF3' } },
                        border: {
                            top: { style: 'thin' }, left: { style: 'thin' },
                            bottom: { style: 'thin' }, right: { style: 'thin' }
                        }
                    },
                    encabezado: {
                        font: { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFF' } },
                        alignment: { horizontal: 'center', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F4F83' } },
                        border: {
                            top: { style: 'thin' }, left: { style: 'thin' },
                            bottom: { style: 'thin' }, right: { style: 'thin' }
                        }
                    },
                    datoTexto: {
                        font: { name: 'Calibri', size: 10, color: { argb: '000000' } },
                        alignment: { horizontal: 'left', vertical: 'middle' },
                        border: {
                            top: { style: 'thin' }, left: { style: 'thin' },
                            bottom: { style: 'thin' }, right: { style: 'thin' }
                        }
                    },
                    datoNumero: {
                        font: { name: 'Calibri', size: 10, color: { argb: '000000' } },
                        alignment: { horizontal: 'right', vertical: 'middle' },
                        numFmt: '#,##0.00',
                        border: {
                            top: { style: 'thin' }, left: { style: 'thin' },
                            bottom: { style: 'thin' }, right: { style: 'thin' }
                        }
                    },
                    filaTotalTexto: {
                        font: { name: 'Calibri', size: 10, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'left', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EEF4F8' } },
                        border: {
                            top: { style: 'thin' }, left: { style: 'thin' },
                            bottom: { style: 'thin' }, right: { style: 'thin' }
                        }
                    },
                    filaTotalNumero: {
                        font: { name: 'Calibri', size: 10, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'right', vertical: 'middle' },
                        numFmt: '#,##0.00',
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EEF4F8' } },
                        border: {
                            top: { style: 'thin' }, left: { style: 'thin' },
                            bottom: { style: 'thin' }, right: { style: 'thin' }
                        }
                    }
                }

                const usuario = this.$store?.state?.userName || 'Usuario'
                const fechaGeneracion = new Date().toLocaleString('es-CO')
                const nombreReporte = (this.listReporte || []).find(
                    (r) => String(r.reporte) === String(this.selReporte)
                )?.descripcion || `Reporte ${this.selReporte}`

                let currentRow = 1

                // Título
                worksheet.mergeCells(currentRow, 1, currentRow, columnCount)
                worksheet.getCell(currentRow, 1).value = 'RESUMEN DE INGRESOS'
                Object.assign(worksheet.getCell(currentRow, 1), styles.titulo)
                currentRow++

                // Subtítulo
                worksheet.mergeCells(currentRow, 1, currentRow, columnCount)
                worksheet.getCell(currentRow, 1).value =
                    `${nombreReporte} | Vigencia: ${this.selVigencia}`
                Object.assign(worksheet.getCell(currentRow, 1), styles.subtitulo)
                currentRow++

                // Fecha
                worksheet.mergeCells(currentRow, 1, currentRow, columnCount)
                worksheet.getCell(currentRow, 1).value =
                    `Generado: ${fechaGeneracion} por ${usuario}`
                Object.assign(worksheet.getCell(currentRow, 1), styles.subtitulo)
                currentRow++

                // Fila en blanco
                currentRow++

                // Anchos de columna (los definimos una vez)
                worksheet.columns = [
                    { width: 14 },
                    ...municipiosCopia.map(() => ({ width: 20 })),
                    { width: 20 }
                ]

                // Un bloque tras otro
                bloquesCopia.forEach((bloque) => {
                    // Franja de título del bloque
                    worksheet.mergeCells(currentRow, 1, currentRow, columnCount)
                    worksheet.getCell(currentRow, 1).value =
                        `${bloque.codigoLabel} (codigo ${bloque.codigoLabel}-${bloque.descripcion})`
                    Object.assign(worksheet.getCell(currentRow, 1), styles.bloqueTitulo)
                    currentRow++

                    // Encabezados
                    const headerValues = [
                        this.labels.etiqueta,
                        ...municipiosCopia.map((m) => m.nombre),
                        this.labels.totalGeneral
                    ]
                    headerValues.forEach((val, idx) => {
                        const cell = worksheet.getCell(currentRow, idx + 1)
                        cell.value = val
                        Object.assign(cell, styles.encabezado)
                    })
                    currentRow++

                    // Filas
                    bloque.items.forEach((fila) => {
                        const esTotal = fila._isTotal === true

                        // Celda etiqueta
                        const celdaEtiqueta = worksheet.getCell(currentRow, 1)
                        celdaEtiqueta.value = fila.etiqueta
                        Object.assign(
                            celdaEtiqueta,
                            esTotal ? styles.filaTotalTexto : styles.datoTexto
                        )

                        // Celdas de municipios
                        municipiosCopia.forEach((m, idx) => {
                            const celda = worksheet.getCell(currentRow, idx + 2)
                            celda.value = Number(fila.valores?.[m.municipio]) || 0
                            Object.assign(
                                celda,
                                esTotal ? styles.filaTotalNumero : styles.datoNumero
                            )
                        })

                        // Celda total
                        const celdaTotal = worksheet.getCell(currentRow, columnCount)
                        celdaTotal.value = Number(fila.total) || 0
                        Object.assign(
                            celdaTotal,
                            esTotal ? styles.filaTotalNumero : styles.datoNumero
                        )

                        currentRow++
                    })

                    // Fila en blanco entre bloques
                    currentRow++
                })

                // Congelar cabecera
                worksheet.views = [{ state: 'frozen', ySplit: 4 }]

                // Generar y descargar
                const buffer = await workbook.xlsx.writeBuffer()
                const blob = new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                })

                const a = document.createElement('a')
                const fechaStr = new Date().toISOString().slice(0, 10)
                a.href = URL.createObjectURL(blob)
                a.download = `ResumenIngresos_${this.selVigencia}_${fechaStr}.xlsx`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(a.href)

                this.$toast.success('Excel exportado exitosamente')
            } catch (error) {
                console.error('Error al exportar Excel:', error)
                this.$toast.error('Error al exportar el Excel: ' + error.message)
            } finally {
                this._exportandoFlag = false
            }
        }
    },
    mounted() {
        // Bandera no reactiva para evitar doble clic durante exportación
        this._exportandoFlag = false

        // TODO: reemplazar por el nit real desde el store
        this.nit = this.$store.state.companyId || '22222222'
        this.cargarCombos()
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

/* ====== TARJETA DE RESULTADOS ====== */
.resumen-card {
    background: #fff !important;
    border-radius: 30px !important;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12) !important;
    padding: 40px !important;
}

/* ====== BARRA DE ACCIONES DENTRO DE LA TARJETA ====== */
.resumen-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    gap: 12px;
}

.resumen-toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.table-toolbar-export-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #1f4f83 !important;
    color: white !important;
    border: 1px solid #1f4f83 !important;
}

.table-toolbar-export-btn:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.table-toolbar-export-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* ====== SCROLL INTERNO ====== */
.bloques-scroll {
    max-height: 70vh;
    overflow-y: auto;
    padding-right: 8px;
}

.bloques-scroll::-webkit-scrollbar {
    width: 8px;
}
.bloques-scroll::-webkit-scrollbar-thumb {
    background: #c7d0d9;
    border-radius: 4px;
}
.bloques-scroll::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}

/* ====== NEUTRALIZAR EL "CONTORNO TARJETA" DE CorporateDataTable ====== */
.resumen-bloque ::v-deep .table-corporate-shell,
.resumen-bloque ::v-deep .table-corporate-body,
.resumen-bloque ::v-deep .tblEstandar,
.resumen-bloque ::v-deep .tblCorporate,
.resumen-bloque ::v-deep .b-table-sticky-header,
.resumen-bloque ::v-deep .table-responsive,
.resumen-bloque ::v-deep .card,
.resumen-bloque ::v-deep .admin-table-block,
.resumen-bloque ::v-deep .corporate-data-table {
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    max-width: 100% !important;
}

.resumen-bloque ::v-deep .table-corporate-shell,
.resumen-bloque ::v-deep .table-corporate-body,
.resumen-bloque ::v-deep .table-responsive,
.resumen-bloque ::v-deep .tblEstandar,
.resumen-bloque ::v-deep .tblCorporate {
    margin: 0 !important;
    padding: 0 !important;
}

.resumen-bloque ::v-deep table.table {
    margin-bottom: 0 !important;
}

.resumen-bloque ::v-deep .table-corporate-toolbar {
    display: none !important;
}

.resumen-bloque ::v-deep .table-corporate-body {
    min-height: 0 !important;
    height: auto !important;
}

/* Matar cualquier altura fija o mínima */
.resumen-bloque ::v-deep .table-corporate-shell,
.resumen-bloque ::v-deep .table-corporate-body,
.resumen-bloque ::v-deep .table-responsive,
.resumen-bloque ::v-deep .b-table-sticky-header,
.resumen-bloque ::v-deep .tblEstandar,
.resumen-bloque ::v-deep .tblCorporate,
.resumen-bloque ::v-deep table {
    min-height: 0 !important;
    height: auto !important;
    max-height: none !important;
}

/* Sin efecto hover en las filas */
.resumen-bloque ::v-deep tbody tr:hover,
.resumen-bloque ::v-deep tbody tr:hover > td,
.resumen-bloque ::v-deep tbody tr:hover > th,
.resumen-bloque ::v-deep tbody tr:hover > * {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    box-shadow: none !important;
    color: inherit !important;
    --bs-table-accent-bg: transparent !important;
    --bs-table-hover-bg: transparent !important;
}

.resumen-bloque ::v-deep tbody tr {
    cursor: default !important;
}

/* ====== BLOQUES PEGADOS ====== */
.resumen-bloque {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
}

.resumen-bloque + .resumen-bloque {
    margin-top: 8px;
}

.bloque-titulo {
    background: #d9eaf3;
    padding: 8px 12px;
    border-radius: 4px;
    font-weight: 600;
    margin: 0 0 4px 0;
}

.fila-total {
    background: #eef4f8;
}

@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    .niif-header .filters-container {
        gap: 4px;
    }
    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
}
</style>